import type { Filter } from '@repo/api-codegen';

/**
 * Mapping of filter operators to their logical opposites.
 * For comparison operators like gte/lte, they swap with their opposites.
 * For other operators, they are wrapped in NOT.
 */
const OPERATOR_COUNTERPARTS: Record<string, string> = {
	// Comparison operators that have direct opposites
	gte: 'lt',
	gt: 'lte',
	lte: 'gt',
	lt: 'gte',

	// Operators that invert to themselves (will be wrapped in NOT)
	exact: 'exact',
	contains: 'contains',
	in: 'in',
	is_set: 'is_set',
	is_null: 'is_null',
	starts_with: 'starts_with',
	ends_with: 'ends_with',
	regex: 'regex',
};

/**
 * Recursively inverts the operators in a Filter object to their logical opposites.
 *
 * For most operators, this wraps them in a NOT operator. For comparison operators
 * like gte/lte, it swaps them with their opposites.
 *
 * Expected behavior:
 * - IS_SET becomes NOT(IS_SET)
 * - CONTAINS becomes NOT(CONTAINS)
 * - GTE becomes LT
 * - EXACT becomes NOT(EXACT)
 * - NOT(EXACT) becomes EXACT (NOT is removed)
 *
 * Logical operators (AND, OR) are processed on their operands.
 * NOT operators are removed if there is only one operand, and if otherwise is converted to OR.
 *
 * @param catalogFilter - The Filter to invert
 * @returns A new Filter with inverted operators
 * @throws Error if an unknown operator is encountered
 */
export function invertCatalogFilterOperators(catalogFilter: Filter): Filter {
	// Handle NOT operator
	if (catalogFilter.operator === 'not') {
		if (catalogFilter.operands && catalogFilter.operands.length === 1) {
			// NOT with single operand - return the operand without NOT
			return catalogFilter.operands[0];
		} else if (catalogFilter.operands && catalogFilter.operands.length > 1) {
			// NOT with multiple operands - convert to OR with inverted operands
			return {
				operator: 'or',
				operands: catalogFilter.operands.map((operand) =>
					invertCatalogFilterOperators(operand)
				),
				field: catalogFilter.field,
				value: catalogFilter.value,
				sub_value: catalogFilter.sub_value,
			};
		} else {
			return catalogFilter;
		}
	}

	if (catalogFilter.operator === 'and' || catalogFilter.operator === 'or') {
		return {
			operator: catalogFilter.operator,
			operands:
				catalogFilter.operands?.map((operand) =>
					invertCatalogFilterOperators(operand)
				) || [],
			field: catalogFilter.field,
			value: catalogFilter.value,
			sub_value: catalogFilter.sub_value,
		};
	}

	const { operator } = catalogFilter;

	if (!(operator in OPERATOR_COUNTERPARTS)) {
		throw new Error(`Unknown operator: ${operator}. Cannot invert filter.`);
	}

	const counterpartOperator = OPERATOR_COUNTERPARTS[operator];

	if (counterpartOperator === operator) {
		return {
			operator: 'not',
			operands: [catalogFilter],
			field: null,
			value: null,
			sub_value: null,
		};
	}

	return {
		...catalogFilter,
		operator: counterpartOperator as Filter['operator'],
	};
}
